import React, { useMemo } from 'react';
import { formatComplaintResolution } from '@/complaints/utils';
import { COMMA_LIST_SEPARATOR, complaintTypeLabelFactory } from '@/constants';
import styled from 'styled-components';
import {
  LogEventDetailsText,
  LogEventDetailsWrapper,
} from './log-details-common';

const ComplaintDetails = styled.table`
  border-collapse: separate;
  border-spacing: 0 5px;
  margin-bottom: 5px;

  tr > td:nth-child(1) {
    min-width: 120px;
  }
`;

const ResolutionDetail = styled.table`
  border-collapse: separate;
  border-spacing: 0 5px;
  margin-bottom: 5px;

  tr > td:nth-child(1) {
    min-width: 120px;
  }

  td {
    display: grid;
  }
`;

const DescriptionHeading = styled.div`
  margin-bottom: 5px;
`;

const CallCentreResolutionHeading = styled.div`
  margin-bottom: 5px;
  margin-top: 10px;
`;

/**
 * Complaint creation activities coming from the stroracle-api are
 * expected to have a field `complaintDetails` in their `value` which
 * contains these fields
 * @typedef {Object} ActivityComplaintDetails
 * @property {number} complaintId - Numeric ID of the complaint
 * @property {string} contactFirstName - Complainant first name
 * @property {string} contactLastName - Complainant last name
 * @property {string} contactPhone - Complainant phone number
 * @property {string} contactEmail - Complainant email
 * @property {string[]} complaintTypes - Complaint types of this complaint
 * @property {string} description - Complaint text
 */

/**
 * Renders a box with complaint details for activities corresponding
 * to new complaints being raised
 * @param {ActivityComplaintDetails} props.complaintDetails Fields of the complaint that was raised
 * @returns {JSX.Element}
 * @constructor
 */
export function ComplaintDetailsLog(props) {
  const { complaintDetails } = props;
  const {
    contactFirstName,
    contactLastName,
    contactPhone,
    contactEmail,
    complaintTypes,
    description,
    callCentreResolution,
    priorComplaintId,
  } = complaintDetails;

  const complainantDetails = [
    `${contactFirstName} ${contactLastName}`,
    contactPhone,
    contactEmail,
  ]
    .map((detail) => detail.trim())
    .filter(Boolean);

  const formattedResolution = useMemo(
    () => formatComplaintResolution(callCentreResolution),
    [callCentreResolution]
  );

  return (
    <LogEventDetailsWrapper>
      <LogEventDetailsText>
        <ComplaintDetails>
          <tbody>
            <tr>
              <td>Complainant</td>
              <td>{complainantDetails.join(COMMA_LIST_SEPARATOR)}</td>
            </tr>
            <tr>
              <td>Complaint Type</td>
              <td>
                {complaintTypes
                  .map(complaintTypeLabelFactory)
                  .join(COMMA_LIST_SEPARATOR)}
              </td>
            </tr>
            {Boolean(priorComplaintId) && (
              <tr>
                <td>Prior Complaint ID</td>
                <td>{priorComplaintId}</td>
              </tr>
            )}
          </tbody>
        </ComplaintDetails>

        <DescriptionHeading>Description</DescriptionHeading>
        <p>&ldquo;{description}&rdquo;</p>
        {callCentreResolution && (
          <ResolutionDetail>
            <CallCentreResolutionHeading>
              Call Center Resolution
            </CallCentreResolutionHeading>
            <tbody>
              <tr>
                <td>
                  {!formattedResolution ? '' : `"${formattedResolution}"`}
                </td>
              </tr>
            </tbody>
          </ResolutionDetail>
        )}
      </LogEventDetailsText>
    </LogEventDetailsWrapper>
  );
}
